import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./styles/index.css";
import "./styles/main.css";
import { RecoilRoot } from "recoil";
import { MsalProvider } from "@azure/msal-react";
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  PublicClientApplication,
} from "@azure/msal-browser";
import { msalConfig } from "./authConfig.ts";

const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.initialize().then(() => {
  // Initialize our auth service with the MSAL instance

  const domNode = document.getElementById("root");
  const root = createRoot(domNode!);
  // Default to using the first account if no account is active on page load
  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  // Optional - This will update account state if a user signs in from another tab or window
  msalInstance.enableAccountStorageEvents();
  msalInstance.addEventCallback(async (event: EventMessage) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS
    ) {
      const payload = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(payload?.account);
    } else if (
      event.eventType === EventType.ACQUIRE_TOKEN_FAILURE ||
      event.eventType === EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE ||
      event.eventType === EventType.SSO_SILENT_FAILURE
    ) {
      if (event.error?.name !== "InteractionRequiredAuthError") {
        await msalInstance.logoutRedirect();
      }
    } else if (event.eventType === EventType.LOGIN_FAILURE) {
      if (event.error?.name === "BrowserAuthError") {
        await msalInstance.loginRedirect();
      } else {
        await msalInstance.logoutRedirect();
      }
    }
  });

  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <RecoilRoot>
          <App />
        </RecoilRoot>
      </MsalProvider>
    </React.StrictMode>
  );
});
